import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  CardMedia,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCart } from 'services/cart/CartContext';
import { useAuth } from 'services/auth/authContext';
import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { validateCoupon } from 'services/firebase/couponService';
import { ICouponValidationResult } from 'utils/types/Coupon';

import styles from './styles';

const OrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart } = useCart();
  const { currentUser } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] =
    useState<ICouponValidationResult | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Check if user is logged in
  const isLoggedIn = !!currentUser;

  // Real coupon validation with Firebase
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Digite um código de cupom');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);
    setCouponValidation(null);

    try {
      const validation = await validateCoupon(
        couponCode,
        cart.totalValue,
        currentUser?.uid,
        hasActiveSubscription,
      );

      if (validation.isValid) {
        setCouponValidation(validation);
        setCouponError(null);
      } else {
        setCouponError(validation.error || 'Cupom inválido');
        setCouponValidation(null);
      }
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      setCouponError('Erro ao validar cupom. Tente novamente.');
      setCouponValidation(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponValidation(null);
    setCouponError(null);
  };

  const discountAmount = couponValidation?.discountAmount || 0;

  const handleProceed = useCallback(() => {
    if (!isLoggedIn) {
      // Redirect to login if user is not logged in
      navigate('/login');
      return;
    }

    // Navigate to shipping options page
    navigate('/beer-store-shipping');
  }, [isLoggedIn, navigate]);

  const handleContinueShopping = () => {
    navigate('/beer-store');
  };

  const handleIncreaseQuantity = (beerId: string) => {
    const item = cart.items.find(item => item.beer.id === beerId);
    if (item) {
      updateQuantity(beerId, item.quantity + 1);
    }
  };

  const handleDecreaseQuantity = (beerId: string) => {
    const item = cart.items.find(item => item.beer.id === beerId);
    if (item && item.quantity > 1) {
      updateQuantity(beerId, item.quantity - 1);
    }
  };

  const handleRemoveItem = (beerId: string) => {
    removeFromCart(beerId);
  };

  if (cart.items.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography variant="h5" sx={styles.emptyMessage}>
          Seu carrinho está vazio
        </Typography>
        <Button
          variant="contained"
          onClick={handleContinueShopping}
          sx={styles.shopButton}
        >
          Continuar Comprando
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h4" sx={styles.title}>
        MEU CARRINHO
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {/* Left side - Items list */}
        <Grid item xs={12} md={6}>
          <List sx={styles.itemsList}>
            {cart.items.map(item => (
              <Card key={item.id} sx={styles.itemCard}>
                <ListItem sx={styles.listItem}>
                  <ListItemAvatar>
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.beer.image}
                      alt={item.beer.name}
                      sx={styles.beerImage}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="h6" sx={styles.beerName}>
                        {item.beer.name.toUpperCase()}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={styles.originalPrice}>
                          Preço Normal: R${' '}
                          {item.beer.price.toFixed(2).replace('.', ',')}
                        </Typography>
                        <Typography variant="body2" sx={styles.premiumPrice}>
                          Preço Premium: R${' '}
                          {item.beer.subscriberPrice
                            .toFixed(2)
                            .replace('.', ',')}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={styles.itemActions}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={styles.excludeButton}
                      onClick={() => handleRemoveItem(item.beer.id)}
                    >
                      EXCLUIR
                    </Button>
                    <Box sx={styles.quantityControls}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleDecreaseQuantity(item.beer.id)}
                        disabled={item.quantity <= 1}
                      >
                        -
                      </Button>
                      <Typography sx={styles.quantity}>
                        {item.quantity}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleIncreaseQuantity(item.beer.id)}
                      >
                        +
                      </Button>
                    </Box>
                  </Box>
                </ListItem>
              </Card>
            ))}
          </List>
        </Grid>

        {/* Right side - Summary and coupon */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.summaryCard}>
            <CardContent>
              {/* Coupon section */}
              <Box sx={styles.couponSection}>
                <Typography variant="h6" sx={styles.couponTitle}>
                  Você tem um cupom?
                </Typography>
                <Box sx={styles.couponInputContainer}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Digite seu cupom"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value)}
                    sx={styles.couponInput}
                    error={!!couponError}
                    disabled={isValidatingCoupon}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        handleApplyCoupon();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleApplyCoupon}
                    sx={styles.couponButton}
                    disabled={isValidatingCoupon || !couponCode.trim()}
                  >
                    {isValidatingCoupon ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'APLICAR'
                    )}
                  </Button>
                </Box>

                {/* Error message */}
                {couponError && (
                  <Alert severity="error" sx={styles.couponAlert}>
                    {couponError}
                  </Alert>
                )}

                {/* Success message */}
                {couponValidation?.isValid && (
                  <Box sx={styles.couponSuccessContainer}>
                    <Chip
                      label={`${couponValidation.coupon?.description || 'Desconto aplicado'} - ${
                        couponValidation.coupon?.type === 'percentage'
                          ? `${(couponValidation.discountPercentage! * 100).toFixed(0)}%`
                          : `R$ ${couponValidation.discountAmount!.toFixed(2).replace('.', ',')}`
                      }`}
                      color="success"
                      sx={styles.discountChip}
                      onDelete={handleRemoveCoupon}
                    />
                  </Box>
                )}
              </Box>

              <Divider sx={styles.divider} />

              {/* Price summary */}
              <Box sx={styles.priceSection}>
                <Box sx={styles.priceRow}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6">
                    R$ {cart.totalValue.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>

                {discountAmount > 0 && (
                  <>
                    <Box sx={styles.priceRow}>
                      <Typography variant="body2" sx={styles.discountText}>
                        Desconto aplicado ({couponValidation?.coupon?.code})
                      </Typography>
                      <Typography variant="body2" sx={styles.discountAmount}>
                        -R$ {discountAmount.toFixed(2).replace('.', ',')}
                      </Typography>
                    </Box>
                    <Divider sx={styles.divider} />
                  </>
                )}

                <Box sx={styles.subscriptionSection}>
                  {!isLoggedIn || !hasActiveSubscription ? (
                    // Show "Assine agora e pague" for non-subscribers
                    <>
                      <Button
                        variant="text"
                        sx={styles.subscriptionLink}
                        onClick={() => navigate('/')}
                      >
                        Assine agora e pague:
                      </Button>
                      <Typography variant="h5" sx={styles.subscriptionPrice}>
                        R${' '}
                        {(discountAmount > 0
                          ? cart.subscriberTotalValue - discountAmount
                          : cart.subscriberTotalValue
                        )
                          .toFixed(2)
                          .replace('.', ',')}
                      </Typography>
                    </>
                  ) : (
                    // Show "Você pagará" for active subscribers
                    <>
                      <Typography variant="h6" sx={styles.subscriberLabel}>
                        Você pagará:
                      </Typography>
                      <Typography variant="h5" sx={styles.subscriptionPrice}>
                        R${' '}
                        {(discountAmount > 0
                          ? cart.subscriberTotalValue - discountAmount
                          : cart.subscriberTotalValue
                        )
                          .toFixed(2)
                          .replace('.', ',')}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              {/* Action buttons */}
              <Box sx={styles.actionButtons}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleContinueShopping}
                  sx={styles.continueShoppingButton}
                >
                  Comprar Mais
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleProceed}
                  sx={styles.proceedButton}
                >
                  AVANÇAR
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderConfirmation;
